import { BaseApiService } from './base-api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ParkbaseStorageService } from 'src/app/@shared/services/parkbaseStorage.service';
import { Terminal, TerminalSummaryPerTerminalType } from '../domain/interfaces/terminal.interface';
import { TerminalActionResponse } from '../domain/interfaces/terminal-action.interface';
import { TerminalResponse } from '../domain/interfaces/terminal-response.interface';
import { ConfigService } from 'src/app/@shared/services/config.service';
import { TerminalTypeSummary } from '../domain/interfaces/terminal-request.interface';

const terminal = {
  availableActions: [],
  id: 0,
  name: '',
  status: { id: 0, name: '', color: 0 },
  type: { id: 0, name: '' },
};

@Injectable({
  providedIn: 'root',
})
export class TerminalsApiService extends BaseApiService {
  private _terminal = new BehaviorSubject<Terminal>(terminal);
  readonly terminal = this._terminal.asObservable();
  private _terminals = new BehaviorSubject<TerminalResponse>({});
  readonly terminals = this._terminals.asObservable();
  private _terminalsSummary = new BehaviorSubject<TerminalSummaryPerTerminalType[]>([]);
  readonly terminalsSummary = this._terminalsSummary.asObservable();
  // TODO: centralized store
  private _dataStore: {
    terminal: Terminal;
    terminals: TerminalResponse;
    terminalsSummary: TerminalSummaryPerTerminalType[];
  } = {
    terminal: terminal,
    terminals: {},
    terminalsSummary: [],
  };

  constructor(
    configService: ConfigService,
    httpClient: HttpClient,
    parkbaseStorageService: ParkbaseStorageService
  ) {
    super(configService, httpClient);
    super.setStorage(parkbaseStorageService);
  }

  /**
   * GET /api/gui/v1.0/terminals/{id}
   * @param id terminal to load by id
   * @returns void
   */
  loadTerminal(id: number): void {
    if (!id) {
      throw new Error('id not provided');
    }
    const apiUrl = this.parkbaseBl9BaseUrl + '/terminals/' + id;
    this.http.get<Terminal>(apiUrl, { headers: this.setHeaders() }).subscribe(
      (data) => {
        this._dataStore.terminal = data;
        this._terminal.next(Object.assign({}, this._dataStore).terminal);
      },
      (error) => this.handleError('Could not load terminal.', error)
    );
  }

  /**
   * GET /api/gui/v1.0/terminals/{id}
   * @param id terminal to load by id
   * @returns Observable<Terminal>
   */
  loadTerminalById(id: number): Observable<Terminal> {
    if (!id) {
      throw new Error('id not provided');
    }
    const apiUrl = this.parkbaseBl9BaseUrl + '/terminals/' + id;
    return this.http.get<Terminal>(apiUrl, { headers: this.setHeaders() });
  }

  /**
   * POST /api/gui/v1.0/terminals
   */
  loadTerminals(): void {
    const apiUrl = this.parkbaseBl9BaseUrl + '/terminals';
    const body = {
      query: {
        page: 1,
        pageSize: 25,
      },
    };
    const headers = this.setHeaders();
    this.http.post<TerminalResponse>(apiUrl, body, { headers }).subscribe(
      (data) => {
        this._dataStore.terminals = data;
        this._terminals.next(Object.assign({}, this._dataStore).terminals);
      },
      (error) => this.handleError('Could not load terminals.', error)
    );
  }

  /**
   * GET /api/gui/v1.0/terminals/summary
   */
  loadTerminalsSummary(): Observable<TerminalTypeSummary[]> {
    const apiUrl = this.parkbaseBl9BaseUrl + '/terminals/summary';
    const headers = this.setHeaders();
    return this.http
      .get<TerminalTypeSummary[]>(apiUrl, {
        headers,
      })
      .pipe(retry(2), catchError(this.getError));
  }

  /**
   * POST /api/gui/v1.0/terminals/{id}/action
   */
  sendTerminalAction(id: number, terminalAction: any): Observable<TerminalActionResponse> {
    if (!id) {
      throw new Error('id not provided');
    }
    const apiUrl = this.parkbaseBl9BaseUrl + '/terminals/' + id + '/action';
    const headers = this.setHeaders();
    this.logger.info('Terminal action' + terminalAction.toString() + ' sent to terminal', id);
    return this.http
      .post<TerminalActionResponse>(apiUrl, terminalAction, {
        headers,
      })
      .pipe(retry(2), catchError(this.getError));
  }
}
